/**
 * Route path constants for StonkFU
 *
 * Single source of truth for all application routes.
 * These constants should be referenced by the router, sidebar, and feature code
 * to prevent drift and make refactors straightforward.
 */

// Base namespaces
export const APP_BASE_PATH = "/app" as const;

/**
 * Application (authenticated) routes under /app
 */
export const PATHS = {
  base: APP_BASE_PATH,

  // Home
  dashboard: `${APP_BASE_PATH}/dashboard`,

  // Portfolio
  portfolio: {
    root: `${APP_BASE_PATH}/portfolio`,
    holdings: `${APP_BASE_PATH}/portfolio/holdings`,
    transactions: `${APP_BASE_PATH}/portfolio/transactions`,
    sectors: `${APP_BASE_PATH}/portfolio/sectors`,
    performance: `${APP_BASE_PATH}/portfolio/performance`,
    risk: `${APP_BASE_PATH}/portfolio/risk`,
  },

  // Dividends
  dividends: {
    root: `${APP_BASE_PATH}/dividends`,
    calendar: `${APP_BASE_PATH}/dividends/calendar`,
    performance: `${APP_BASE_PATH}/dividends/performance`,
    incomeLab: `${APP_BASE_PATH}/dividends/income-lab`,
  },

  // Analytics
  analytics: {
    root: `${APP_BASE_PATH}/analytics`,
    income: `${APP_BASE_PATH}/analytics/income`,
    aiInsights: `${APP_BASE_PATH}/analytics/ai-insights`,
    widgetLab: `${APP_BASE_PATH}/analytics/widget-lab`,
    reports: `${APP_BASE_PATH}/analytics/reports`,
  },

  // Social
  social: {
    root: `${APP_BASE_PATH}/social`,
    findUsers: `${APP_BASE_PATH}/social/find-users`,
    following: `${APP_BASE_PATH}/social/following`,
    groups: `${APP_BASE_PATH}/social/groups`,
  },

  // Setup
  setup: {
    root: `${APP_BASE_PATH}/setup`,
    importData: `${APP_BASE_PATH}/setup/import`,
    exportData: `${APP_BASE_PATH}/setup/export`,
    linkBroker: `${APP_BASE_PATH}/setup/link-broker`,
    linkBank: `${APP_BASE_PATH}/setup/link-bank`,
    backups: `${APP_BASE_PATH}/setup/backups`,
  },

  // Settings
  settings: {
    root: `${APP_BASE_PATH}/settings`,
    profile: `${APP_BASE_PATH}/settings/profile`,
    preferences: `${APP_BASE_PATH}/settings/preferences`,
    subscription: `${APP_BASE_PATH}/settings/subscription`,
    security: `${APP_BASE_PATH}/settings/security`,
    notifications: `${APP_BASE_PATH}/settings/notifications`,
  },
} as const;

/**
 * Public routes (unauthenticated) typically mounted outside /app
 */
export const PUBLIC_PATHS = {
  landing: "/landing" as const,
  auth: {
    base: "/auth" as const,
    login: "/auth/login" as const,
    register: "/auth/register" as const,
    callback: "/auth/callback" as const,
    logout: "/auth/logout" as const,
    forgetPassword: "/auth/forget-password" as const,
    resetPassword: "/auth/reset-password" as const,
    lockScreen: "/auth/lock-screen" as const,
  },
  errors: {
    error404: "/error-404" as const,
    error500: "/error-500" as const,
  },
} as const;

// Utility types
type Primitive = string | number | boolean | symbol | bigint | null | undefined;
type LeafPaths<T> = T extends Primitive
  ? never
  : {
      [K in keyof T]: T[K] extends string
        ? T[K]
        : T[K] extends Record<string, unknown>
        ? LeafPaths<T[K]>
        : never;
    }[keyof T];

/**
 * Union of all app route strings (e.g., "/app/dashboard" | "/app/portfolio/holdings" | ...)
 */
export type AnyAppPath = LeafPaths<typeof PATHS>;

/**
 * Union of all public route strings
 */
export type AnyPublicPath = LeafPaths<typeof PUBLIC_PATHS>;

/**
 * Recursively collects path strings from a nested object of paths.
 */
export function collectPaths(obj: Record<string, unknown>): string[] {
  const results: string[] = [];
  const walk = (node: unknown) => {
    if (typeof node === "string") {
      results.push(node);
      return;
    }
    if (node && typeof node === "object") {
      for (const value of Object.values(node)) {
        walk(value);
      }
    }
  };
  walk(obj);
  return results;
}

/**
 * Convenience arrays if you need to iterate or validate routes.
 */
export const ALL_APP_PATHS: readonly string[] = Object.freeze(collectPaths(PATHS));
export const ALL_PUBLIC_PATHS: readonly string[] = Object.freeze(collectPaths(PUBLIC_PATHS));


